import {
  Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Navigations that resolve inside this window never show the bar — most
 * route changes here are in-memory (no HTTP wait) and finish well under
 * 150ms, so showing+hiding a bar for every click would just be a flash.
 * `PlanResolver`'s HTTP-backed navigations comfortably clear this delay.
 */
const SHOW_DELAY_MS = 150;

/**
 * Thin fixed top bar that appears only for slow (resolver-blocked) route
 * transitions — e.g. clicking a plan row, which runs `PlanResolver` and
 * blocks route activation on an HTTP call with no other visible feedback.
 * Mounted once in the app shell.
 */
@Component({
  selector: 'sp-nav-progress',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="nav-progress" [class.nav-progress--active]="visible">
      <div class="nav-progress__bar"></div>
    </div>
  `,
  styles: [`
    .nav-progress {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      z-index: 9999;
      overflow: hidden;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.15s ease;
    }

    .nav-progress--active {
      opacity: 1;
    }

    .nav-progress__bar {
      height: 100%;
      width: 35%;
      background: var(--color-primary);
      animation: nav-progress-slide 1.1s ease-in-out infinite;
    }

    @keyframes nav-progress-slide {
      0%   { margin-left: -35%; }
      100% { margin-left: 100%; }
    }
  `],
})
export class NavProgressComponent implements OnInit, OnDestroy {
  visible = false;

  private readonly destroy$ = new Subject<void>();
  private delayTimer: Subscription | null = null;

  constructor(
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          // Cancel any timer left over from a prior navigation, then arm a
          // fresh one — the bar only appears once this navigation itself
          // has been pending past the delay guard.
          this.clearDelayTimer();
          this.delayTimer = timer(SHOW_DELAY_MS).subscribe(() => {
            this.visible = true;
            this.cdr.markForCheck();
          });
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          // Cancelling the pending timer here is what prevents the flash:
          // a navigation that finishes inside the delay window never gets
          // the chance to flip `visible` to true. A cancelled/errored
          // navigation must not leave the bar stuck on screen either.
          this.clearDelayTimer();
          if (this.visible) {
            this.visible = false;
            this.cdr.markForCheck();
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.clearDelayTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private clearDelayTimer(): void {
    this.delayTimer?.unsubscribe();
    this.delayTimer = null;
  }
}
